import type { ImportRegister } from './types';

/** Canonical values from live-schema-2026-09-16.sql lines 629334-629338. */
export const SCREENING_TYPES = [
  'NATIONAL_POLICE_CHECK',
  'NDIS_WORKER_SCREENING',
  'AGED_CARE_WORKER_SCREENING',
  'WORKING_WITH_CHILDREN',
  'AHPRA_REGISTRATION',
  'STATUTORY_DECLARATION',
  'INTERNATIONAL_POLICE_CHECK',
  'FIRST_AID',
  'CPR',
] as const;

export const SCREENING_STATUSES = [
  'PENDING',
  'IN_PROGRESS',
  'REQUIRES_REVIEW',
  'CLEARED',
  'CONDITIONAL',
  'EXPIRED',
  'REJECTED',
  'REVOKED',
  'EXCLUDED',
] as const;

export const REQUIRED_FIELDS: Record<ImportRegister, readonly string[]> = {
  WORKERS: ['first_name', 'last_name', 'person_type', 'active_from'],
  SCREENINGS: ['screening_type', 'status'],
  TRAINING: ['training_name', 'completed_on'],
  POLICY_ACKNOWLEDGEMENTS: ['policy_reference', 'acknowledged_on'],
};

/** Canonical optional CSV fields, shared by parser headers and import templates. */
export const OPTIONAL_FIELDS: Record<ImportRegister, readonly string[]> = {
  WORKERS: [
    'preferred_name',
    'date_of_birth',
    'email',
    'phone',
    'role_title',
    'ceased_on',
    'external_person_key',
    'confirm_duplicate',
  ],
  SCREENINGS: [
    'worker_external_key',
    'first_name',
    'last_name',
    'reference_number',
    'issuer',
    'jurisdiction',
    'issued_on',
    'expires_on',
    'no_expiry_reason',
    'confirm_duplicate',
  ],
  // Notes is deliberately capped by the parser. The register does not accept
  // clinical narratives or other free-text personal information.
  TRAINING: [
    'worker_external_key',
    'first_name',
    'last_name',
    'provider',
    'certificate_number',
    'expires_on',
    'hours',
    'delivery_mode',
    'notes',
  ],
  POLICY_ACKNOWLEDGEMENTS: [
    'worker_external_key',
    'first_name',
    'last_name',
    'policy_version',
    'evidence_reference',
    'notes',
  ],
};

export const REGISTER_FIELDS: Record<ImportRegister, readonly string[]> = {
  WORKERS: [...REQUIRED_FIELDS.WORKERS, ...OPTIONAL_FIELDS.WORKERS],
  SCREENINGS: [...REQUIRED_FIELDS.SCREENINGS, ...OPTIONAL_FIELDS.SCREENINGS],
  TRAINING: [...REQUIRED_FIELDS.TRAINING, ...OPTIONAL_FIELDS.TRAINING],
  POLICY_ACKNOWLEDGEMENTS: [
    ...REQUIRED_FIELDS.POLICY_ACKNOWLEDGEMENTS,
    ...OPTIONAL_FIELDS.POLICY_ACKNOWLEDGEMENTS,
  ],
};

export const DATE_FIELDS = [
  'active_from',
  'ceased_on',
  'date_of_birth',
  'issued_on',
  'expires_on',
  'completed_on',
  'acknowledged_on',
];

export function friendlyEnum(raw: string, values: readonly string[]): string | null {
  const canonical = raw
    .trim()
    .toLowerCase()
    .replace(/[ _-]+/g, '');
  return values.find((value) => value.toLowerCase().replace(/_/g, '') === canonical) ?? null;
}
